<template>
	<TooltipComponent
		:tooltip="signedInformation(doc)"
		direction="right"
		maxWidth="450"
	>
		<template #showFromOutside>
			<div
				v-if="doc.status == ENUMS.PaperworkDocumentStatus.ErrorGenerating"
				class="text-error defaultCursor signersText"
			>
				<i class="fa fa-exclamation-circle iconSpacing"></i>
				<span>CAN'T CAPTURE SIGNATURE</span>
			</div>

			<div
				v-else-if="doesntNeedSignatures"
				class="defaultCursor signersText text-success"
			>
				<i class="fa fa-check-circle iconSpacing"></i>
				<span>N/A</span>
			</div>

			<div
				v-else-if="needsWetSignature"
				class="text-error defaultCursor signersText"
			>
				<i class="fa fa-exclamation-circle iconSpacing" />
				<span>REQUIRES WET SIGNATURE</span>
			</div>

			<div
				v-else
				class="text-warning splitlines defaultCursor"
			>
				<div
					v-for="signerId in docSignerIds"
					:key="signerId"
					:class="{ signersText: true, signed: hasSignerSignature(signerId) }"
				>
					<div
						v-if="displayNotaryStamp(signerId)"
						:class="[showAsStamped(signerId) ? 'stamped' : '']"
					>
						<i
							v-if="displayNotaryStamp(signerId)"
							class="fas iconSpacing fa-stamp"
						>
						</i>
						<span :class="[showAsStamped(signerId) ? 'stamped' : '']">{{ getSignerName(signerId) }}</span>
					</div>
					<div v-else>
						<i :class="['fa iconSpacing', hasSignerSignature(signerId) ? 'fa-check-circle' : 'fa-exclamation-circle']" />
						<span>{{ getSignerName(signerId) }}</span>
					</div>
				</div>
			</div>
		</template>
	</TooltipComponent>
</template>
<script lang="ts">
	export interface IDocumentPacketDocumentSignatureProps {
		doc: IPaperworkDocument;
		fimenu: FIMenu;
		signedInformation: (doc: IPaperworkDocument) => string | string[];
	}
</script>
<script setup lang="ts">
	import { getPersonSigningFromDocument, isDocSignedBySignerOrDone, getSignerIdsFromDocument } from '@core/helpers/esign-helper';
	import ENUMS from '@core/classes/Enums';
	import FIMenu from '@core/classes/FIMenu';
	import { IPaperworkDocument } from '@core/classes/Paperwork/PaperworkDocument';
	import { IUserNotaryInformation } from '@core/classes/Notary/NotaryInformation';
	import TooltipComponent from '@core/components/TooltipComponent.vue';
	import { useCurrentUser } from '@core/stores/CurrentUserAuth';

	import { computed } from 'vue';

	const { doc, fimenu, signedInformation } = defineProps<IDocumentPacketDocumentSignatureProps>();

	const currentUser = useCurrentUser();

	const doesntNeedSignatures = computed(() => {
		return !doc?.overlays || doc?.overlays.length <= 0;
	});

	const needsWetSignature = computed(() => {
		// If an Impact Doc has been generated, then this document is not eligible for e-signing.
		if (doc.status === ENUMS.PaperworkDocumentStatus.Signed || !doc.canOnlyWetSign) return false;
		if (doc.status === ENUMS.PaperworkDocumentStatus.Signed || !doc.canOnlyWetSign) return false;
		return !doc.signedDoc;
	});

	const docSignerIds = computed(() => {
		return getSignerIdsFromDocument(doc);
	});
	function hasSignerSignature(signerId: string) {
		if (!doc) return false;

		// If the signer (F&I mgr) is a notary, but the notary stamp should not not displayed, show the doc as signed
		if (signerIsNotary(signerId) && !displayNotaryStamp(signerId)) return true;

		// If we need a wet signature, assume the signed doc is fully signed.
		if (doc.impactDoc && doc.status === ENUMS.PaperworkDocumentStatus.Signed) return true;

		return doc.isWetSigned || isDocSignedBySignerOrDone(doc, signerId);
	}
	function getSignerName(signerId: string) {
		const personSigning = getPersonSigningFromDocument(signerId, doc, fimenu);
		if (personSigning) {
			return personSigning.fullName.toUpperCase();
		}
	}
	function signerIsNotary(signerId: string) {
		return Boolean(doc.overlays.some(o => o.overlayType === ENUMS.OVERLAY_TYPES.NOTARY_STAMP && o.signatureMapping.signerId === signerId));
	}
	function displayNotaryStamp(signerId: string) {
		if (
			signerId !== fimenu.customer.id &&
			signerId !== fimenu.coCustomer.id &&
			!fimenu.customersInfo.customers.some(c => c.id === signerId) &&
			doc.overlays.some(o => o.notaryStamp) &&
			// Check if user's notary state matches store state
			currentUser.userInfo.userNotaryInformation?.some((ni: IUserNotaryInformation) => ni.state === fimenu.store.storeState)
		)
			return true;
	}
	function showAsStamped(signerId: string) {
		// Only show as stamped if the doc is signed
		return displayNotaryStamp(signerId) && doc.status === ENUMS.PaperworkDocumentStatus.Signed;
	}
</script>
<style>
	.stamped {
		color: var(--success-color);
	}
</style>
